import Cookies from 'universal-cookie';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import moment from 'moment';

import { Condition } from 'Component/others';
import useGetGiveawayType from 'Domain/brief-live/useGetGiveawayType';
import usePostUserImage from 'Domain/brief-live/usePostUserImage';
import usePatchUpdateGiveawayItem from 'Domain/brief-live/usePatchUpdateGiveawayItem';
import useDeleteGiveawayItem from 'Domain/brief-live/useDeleteGiveawayItem';
import { useModal } from 'Store/modal';

import GiveawayItem from 'View/brief-live/component/giveaway-item';
import GiveawayViewItem from 'View/brief-live/component/giveaway-view-item';

const parseTimeStringToDate = (timeStr) => {
  if (!timeStr) return null;

  const cookies = new Cookies();
  const timezone = cookies.get('organization')?.timezone;

  const [hours, minutes] = timeStr.split(':');
  const date = moment.tz(`${hours}:${minutes}`, 'HH:mm', timezone);
  return date;
};

const mapGiveawayToFormValues = (giveaway, optionsGiveawayType) => {
  const hasRequiredProducts = (giveaway?.rules?.requiredProducts?.length ?? 0) > 0;
  const isMinPurchase = giveaway?.rules?.type === 'MINIMUM_PURCHASE';

  return {
    giveaways: [
      {
        productName: giveaway?.productName || '',
        totalStock: String(giveaway?.totalStock) || null,
        imagePath: {
          imagePath: giveaway?.imagePath || '',
          key: giveaway?.imageKey || '',
        },
        rules: {
          note: giveaway?.rules?.note || '',
          minimumPurchaseAmount: giveaway?.rules?.minimumPurchaseAmount || null,
          withProductPurchase: giveaway?.rules?.withProductPurchase || (isMinPurchase && hasRequiredProducts),
          type: optionsGiveawayType?.find((o) => o.value === giveaway?.rules?.type) || null,
          requiredProducts: (giveaway?.rules?.requiredProducts || []).map((item) => {
            return {
              imagePath: item?.imagePath,
              imageKey: item?.imageKey,
              productName: item?.productName,
              sku: item?.sku,
              isManual: item?.isManual,
            };
          }),
        },
        sessions:
          giveaway?.sessions?.map((session) => ({
            id: session?.id || '',
            promotionDate: session.promotionDate ? moment(session.promotionDate, 'DD MMMM YY').toDate() : null,
            startTime: parseTimeStringToDate(session.startTime),
            endTime: parseTimeStringToDate(session.endTime),
            allocatedStock: String(session.allocatedStock) || null,
            accountLivestreamId: session.accountLivestream
              ? { id: session.accountLivestream?.id, value: session.accountLivestream?.id, label: session.accountLivestream?.name }
              : null,
          })) || [],
      },
    ],
  };
};

const GiveawayEditableItem = ({ t, giveaway, defaultEditing = false, onCancel, giveawayData }) => {
  const [isEditing, setIsEditing] = useState(defaultEditing);
  const [initialValues, setInitialValues] = useState(mapGiveawayToFormValues(giveaway, []));

  const { onOpenModal, onResetModal } = useModal();
  const { data: optionsGiveawayType } = useGetGiveawayType();
  const { mutateAsync: uploadImage } = usePostUserImage();
  const { mutate: updateGiveawayItem, isPending: isSavePending } = usePatchUpdateGiveawayItem({
    onSuccess: () => setIsEditing(false),
  });
  const { mutate: deleteGiveawayItem } = useDeleteGiveawayItem();

  const formMethods = useForm({
    defaultValues: { giveaways: [{}] },
  });
  const {
    control,
    register,
    setValue,
    watch,
    reset,
    trigger,
    getValues,
    formState: { errors },
  } = formMethods;

  const watchedGiveaways = watch('giveaways') || [];
  const watchedBrand = {
    id: giveawayData?.brand.id,
    value: giveawayData?.brand.name,
    label: giveawayData?.brand.name,
  };

  useEffect(() => {
    if (defaultEditing && !giveaway?.id) {
      const { isNewData, ...newGiveawayData } = giveaway || {};
      reset({ giveaways: [newGiveawayData] });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEdit = () => {
    const values = mapGiveawayToFormValues(giveaway, optionsGiveawayType);
    reset(values);
    setInitialValues(values);
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      setIsEditing(false);
    }
  };

  const handleRemove = () => {
    if (giveaway?.id) {
      onOpenModal({
        width: '400px',
        isOpen: true,
        type: 'cancel',
        title: t('formGiveaway.deleteRewardTitle'),
        contentDescription: t('formGiveaway.deleteRewardDescription'),
        showPrimaryButton: true,
        showSecondaryButton: true,
        primaryButtonText: 'Ya',
        secondaryButtonText: t('formGiveaway.cancelButton'),
        onClickPrimaryButton: () => {
          deleteGiveawayItem({ id: giveaway.id });
          onResetModal();
        },
        onClickSecondaryButton: onResetModal,
      });
    } else {
      handleCancel();
    }
  };

  const handleSave = () => {
    const formValues = getValues();
    updateGiveawayItem({
      id: giveaway?.id,
      form: formValues.giveaways[0],
    });
  };

  return (
    <Condition statement={isEditing} fallback={<GiveawayViewItem t={t} giveaway={giveaway} onEdit={handleEdit} />}>
      <FormProvider {...formMethods}>
        <GiveawayItem
          t={t}
          index={0}
          control={control}
          register={register}
          setValue={setValue}
          errors={errors}
          watchedGiveaways={watchedGiveaways}
          watchedBrand={watchedBrand}
          optionsGiveawayType={optionsGiveawayType}
          uploadImage={uploadImage}
          remove={handleRemove}
          onSave={handleSave}
          onCancel={handleCancel}
          isSavePending={isSavePending}
          trigger={trigger}
          defaultValues={initialValues}
        />
      </FormProvider>
    </Condition>
  );
};

export default GiveawayEditableItem;
